import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw,
  Printer,
  Truck,
  MapPin,
  FileText,
  Search,
  Filter,
  X,
  CheckCircle2,
  Clock,
  User as UserIcon,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import {
  despachoService,
  DespachoCliente,
  DespachoOperacion,
  ModoDespacho,
} from '../services/despachoService';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';

type TabKey = 'vigente' | 'entregados' | 'todos';

interface FiltrosState {
  search: string;
  fecha_desde: string;
  fecha_hasta: string;
  cliente_ids: number[];
  operacion_ids: number[];
}

const FILTROS_VACIOS: FiltrosState = {
  search: '',
  fecha_desde: '',
  fecha_hasta: '',
  cliente_ids: [],
  operacion_ids: [],
};

/**
 * Módulo Despacho (2026-06-15).
 *
 * Tabs:
 *  - Vigente   (default) = registros entregado_a_despacho. Acción
 *                          "Entregar al cliente" + Imprimir.
 *  - Entregados          = entregados al cliente HOY (fecha_hasta=hoy).
 *                          Vista histórica con entregado_por / recibido_por.
 *  - Todos               = vigentes + entregados, con rango de fechas.
 *
 * Filtros (aplican a todas las tabs):
 *  - Búsqueda libre: cliente, faena, código pedido, receta.
 *  - Rango de fechas (por p.fecha_entrega).
 *  - Multi-select de cliente y faena.
 *
 * RBAC: si rol=cliente, sólo lectura (sin botones de entrega), y el
 * backend ya filtra por su cliente_id.
 */
export default function Despacho() {
  const { success: showToast, error: showError } = useToast();
  const { user } = useAuth();
  const esCliente = user?.rol === 'cliente';

  const [tab, setTab] = useState<TabKey>('vigente');
  const [filtros, setFiltros] = useState<FiltrosState>(FILTROS_VACIOS);
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);

  const [data, setData] = useState<DespachoCliente[]>([]);
  const [conteos, setConteos] = useState<{
    vigente: number;
    entregados: number;
  }>({ vigente: 0, entregados: 0 });
  const [loading, setLoading] = useState(false);
  const [trabajando, setTrabajando] = useState<Set<number>>(new Set());

  const [modal, setModal] = useState<{
    isOpen: boolean;
    titulo: string;
    registroIds: number[];
  }>({ isOpen: false, titulo: '', registroIds: [] });
  const [entregaForm, setEntregaForm] = useState({
    entregado_por: '',
    recibido_por: '',
    comentario: '',
  });

  // Fase 6+firmas (2026-07-01): modal para entregar UNA TANDA (día/noche)
  // con captura de nombres. Reusa el mismo shape de firmas pero llama a
  // /despacho/pedido/:id/tanda/:seg/entregar con el payload.
  const [modalTanda, setModalTanda] = useState<{
    isOpen: boolean;
    pedidoId: number | null;
    pedidoCodigo: string;
    segmento: 'dia' | 'noche' | 'transversal';
    cantidadRegistros: number;
  }>({
    isOpen: false,
    pedidoId: null,
    pedidoCodigo: '',
    segmento: 'dia',
    cantidadRegistros: 0,
  });

  const modoBackend: ModoDespacho =
    tab === 'vigente' ? 'vigente' : tab === 'entregados' ? 'entregados' : 'todos';

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const hoy = new Date().toISOString().slice(0, 10);
      const fechaDesdeEfectiva =
        tab === 'entregados' ? hoy : filtros.fecha_desde || '';
      const fechaHastaEfectiva =
        tab === 'entregados' ? hoy : filtros.fecha_hasta || '';

      const res = await despachoService.listar({
        modo: modoBackend,
        search: filtros.search || undefined,
        fecha_desde: fechaDesdeEfectiva || undefined,
        fecha_hasta: fechaHastaEfectiva || undefined,
        cliente_ids: filtros.cliente_ids.length
          ? filtros.cliente_ids
          : undefined,
        operacion_ids: filtros.operacion_ids.length
          ? filtros.operacion_ids
          : undefined,
      });
      setData(res);
    } catch {
      showError('No se pudo cargar el listado de despacho');
    } finally {
      setLoading(false);
    }
  }, [tab, modoBackend, filtros, showError]);

  // Contadores: 2 pings paralelos al backend sin filtros adicionales,
  // para mostrar los badges en las tabs.
  const cargarConteos = useCallback(async () => {
    try {
      const hoy = new Date().toISOString().slice(0, 10);
      const [vig, ent] = await Promise.all([
        despachoService.listar({ modo: 'vigente' }),
        despachoService.listar({
          modo: 'entregados',
          fecha_desde: hoy,
          fecha_hasta: hoy,
        }),
      ]);
      const count = (arr: DespachoCliente[]) =>
        arr.reduce(
          (acc, c) =>
            acc +
            c.operaciones.reduce(
              (a, o) => a + o.pedidos.reduce((b, p) => b + p.registros.length, 0),
              0
            ),
          0
        );
      setConteos({ vigente: count(vig), entregados: count(ent) });
    } catch {
      /* silenciar — los contadores son cosméticos */
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    cargarConteos();
  }, [cargarConteos]);

  const totalActual = useMemo(
    () =>
      data.reduce(
        (acc, c) =>
          acc +
          c.operaciones.reduce(
            (a, o) => a + o.pedidos.reduce((b, p) => b + p.registros.length, 0),
            0
          ),
        0
      ),
    [data]
  );

  // Opciones para multiselect: derivadas del set vigente+entregados-hoy
  // sin filtros, para no perderlas cuando el usuario filtra.
  const [opcionesFiltro, setOpcionesFiltro] = useState<{
    clientes: { id: number; nombre: string }[];
    operaciones: {
      id: number;
      nombre: string;
      cliente_id: number;
      cliente_nombre: string;
    }[];
  }>({ clientes: [], operaciones: [] });

  useEffect(() => {
    (async () => {
      try {
        const todos = await despachoService.listar({ modo: 'todos' });
        const clientesMap = new Map<number, string>();
        const opsMap = new Map<
          number,
          { id: number; nombre: string; cliente_id: number; cliente_nombre: string }
        >();
        for (const c of todos) {
          clientesMap.set(c.cliente_id, c.cliente_nombre);
          for (const o of c.operaciones) {
            opsMap.set(o.operacion_id, {
              id: o.operacion_id,
              nombre: o.operacion_nombre,
              cliente_id: c.cliente_id,
              cliente_nombre: c.cliente_nombre,
            });
          }
        }
        setOpcionesFiltro({
          clientes: Array.from(clientesMap.entries())
            .map(([id, nombre]) => ({ id, nombre }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre)),
          operaciones: Array.from(opsMap.values()).sort((a, b) =>
            a.nombre.localeCompare(b.nombre)
          ),
        });
      } catch {
        /* silenciar */
      }
    })();
  }, []);

  const abrirModalFaena = (
    cliente: DespachoCliente,
    operacion: DespachoOperacion
  ) => {
    const registroIds = operacion.pedidos.flatMap((p) =>
      p.registros.map((r) => r.id)
    );
    setModal({
      isOpen: true,
      titulo: `${cliente.cliente_nombre} — ${operacion.operacion_nombre}`,
      registroIds,
    });
    setEntregaForm({ entregado_por: '', recibido_por: '', comentario: '' });
  };

  const abrirModalCliente = (cliente: DespachoCliente) => {
    const registroIds = cliente.operaciones.flatMap((o) =>
      o.pedidos.flatMap((p) => p.registros.map((r) => r.id))
    );
    setModal({
      isOpen: true,
      titulo: `${cliente.cliente_nombre} (todas las faenas)`,
      registroIds,
    });
    setEntregaForm({ entregado_por: '', recibido_por: '', comentario: '' });
  };

  const confirmarEntregar = async () => {
    if (modal.registroIds.length === 0) return;
    setTrabajando((prev) => {
      const next = new Set(prev);
      modal.registroIds.forEach((id) => next.add(id));
      return next;
    });
    try {
      await Promise.allSettled(
        modal.registroIds.map((id) =>
          despachoService.entregarAlCliente(id, {
            comentario: entregaForm.comentario || undefined,
            entregado_por: entregaForm.entregado_por || undefined,
            recibido_por: entregaForm.recibido_por || undefined,
          })
        )
      );
      const nombres = [
        entregaForm.entregado_por && `Entrega: ${entregaForm.entregado_por}`,
        entregaForm.recibido_por && `Recibe: ${entregaForm.recibido_por}`,
      ]
        .filter(Boolean)
        .join(' · ');
      showToast('Entregado al cliente', nombres || 'Registro guardado');
      setModal({ isOpen: false, titulo: '', registroIds: [] });
      setEntregaForm({ entregado_por: '', recibido_por: '', comentario: '' });
      await Promise.all([cargar(), cargarConteos()]);
    } finally {
      setTrabajando((prev) => {
        const next = new Set(prev);
        modal.registroIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const abrirModalTanda = (
    pedidoId: number,
    pedidoCodigo: string,
    segmento: 'dia' | 'noche' | 'transversal',
    cantidadRegistros: number
  ) => {
    setEntregaForm({ entregado_por: '', recibido_por: '', comentario: '' });
    setModalTanda({
      isOpen: true,
      pedidoId,
      pedidoCodigo,
      segmento,
      cantidadRegistros,
    });
  };

  const cerrarModalTanda = () =>
    setModalTanda({
      isOpen: false,
      pedidoId: null,
      pedidoCodigo: '',
      segmento: 'dia',
      cantidadRegistros: 0,
    });

  const confirmarEntregarTanda = async () => {
    if (!modalTanda.pedidoId) return;
    const { pedidoId, segmento } = modalTanda;
    try {
      await despachoService.entregarTanda(pedidoId, segmento, {
        entregado_por: entregaForm.entregado_por || undefined,
        recibido_por: entregaForm.recibido_por || undefined,
        comentario: entregaForm.comentario || undefined,
      });
      const nombres = [
        entregaForm.entregado_por && `Entrega: ${entregaForm.entregado_por}`,
        entregaForm.recibido_por && `Recibe: ${entregaForm.recibido_por}`,
      ]
        .filter(Boolean)
        .join(' · ');
      showToast(
        `Tanda ${segmento === 'dia' ? 'DÍA' : segmento === 'noche' ? 'NOCHE' : 'TRANSVERSAL'} entregada`,
        nombres || `${modalTanda.cantidadRegistros} registros`
      );
      cerrarModalTanda();
      await Promise.all([cargar(), cargarConteos()]);
    } catch (err) {
      showError(`No se pudo entregar la tanda ${segmento}`);
    }
  };

  const imprimirCliente = (cliente: DespachoCliente) => {
    window.open(`/despacho/imprimir/cliente/${cliente.cliente_id}`, '_blank');
  };

  const imprimirOperacion = (cliente: DespachoCliente, op: DespachoOperacion) => {
    window.open(
      `/despacho/imprimir/operacion/${cliente.cliente_id}/${op.operacion_id}`,
      '_blank'
    );
  };

  // Fase 6 (2026-07-01): imprimir el comprobante FILTRADO a un segmento
  // (`?segmento=dia|noche`). Se usa desde los botones "Imprimir Día/Noche"
  // por pedido cuando la operación es segmentada.
  const imprimirTanda = (
    cliente: DespachoCliente,
    op: DespachoOperacion,
    segmento: 'dia' | 'noche' | 'transversal'
  ) => {
    window.open(
      `/despacho/imprimir/operacion/${cliente.cliente_id}/${op.operacion_id}?segmento=${segmento}`,
      '_blank'
    );
  };

  const toggleId = (lista: number[], id: number) =>
    lista.includes(id) ? lista.filter((x) => x !== id) : [...lista, id];

  const limpiarFiltros = () => setFiltros(FILTROS_VACIOS);
  const hayFiltrosActivos =
    !!filtros.search ||
    !!filtros.fecha_desde ||
    !!filtros.fecha_hasta ||
    filtros.cliente_ids.length > 0 ||
    filtros.operacion_ids.length > 0;

  const fmtFecha = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader
        title={esCliente ? 'Mis Despachos' : 'Despacho'}
        subtitle={
          esCliente
            ? 'Estado de tus pedidos: en camino o ya entregados'
            : 'Pedidos terminados listos para entregar al cliente'
        }
        actions={
          <button
            onClick={() => {
              cargar();
              cargarConteos();
            }}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 bg-[#29ABE2] hover:bg-[#1f8fbf] text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Cargando…' : 'Actualizar'}
          </button>
        }
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-4">
        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
          {(
            [
              { key: 'vigente', label: 'Vigente', icon: Truck, count: conteos.vigente, color: '#29ABE2' },
              { key: 'entregados', label: 'Entregados hoy', icon: CheckCircle2, count: conteos.entregados, color: '#16a34a' },
              { key: 'todos', label: 'Todos', icon: Clock, count: null as number | null, color: '#6b7280' },
            ] as const
          ).map((t) => {
            const active = tab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`relative px-4 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                  active
                    ? 'border-[#29ABE2] text-[#29ABE2]'
                    : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                }`}
              >
                <Icon className="w-4 h-4" style={{ color: active ? undefined : t.color }} />
                {t.label}
                {t.count !== null && (
                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      active
                        ? 'bg-[#29ABE2]/10 text-[#29ABE2]'
                        : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Buscador + toggle filtros */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={filtros.search}
                onChange={(e) =>
                  setFiltros((f) => ({ ...f, search: e.target.value }))
                }
                placeholder="Buscar por cliente, faena, código pedido, receta…"
                className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-[#29ABE2] focus:border-[#29ABE2]"
              />
            </div>
            <button
              onClick={() => setFiltrosAbiertos((v) => !v)}
              className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border ${
                hayFiltrosActivos
                  ? 'border-[#F7941D] bg-[#F7941D]/10 text-[#F7941D]'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtros
              {hayFiltrosActivos && (
                <span className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#F7941D] text-white text-[10px] font-bold">
                  {(filtros.fecha_desde ? 1 : 0) +
                    (filtros.fecha_hasta ? 1 : 0) +
                    (filtros.cliente_ids.length ? 1 : 0) +
                    (filtros.operacion_ids.length ? 1 : 0)}
                </span>
              )}
            </button>
            {hayFiltrosActivos && (
              <button
                onClick={limpiarFiltros}
                className="inline-flex items-center gap-1 px-2 py-2 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-900"
                title="Limpiar filtros"
              >
                <X className="w-3 h-3" /> Limpiar
              </button>
            )}
          </div>

          {filtrosAbiertos && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fecha entrega desde
                </label>
                <input
                  type="date"
                  value={filtros.fecha_desde}
                  onChange={(e) =>
                    setFiltros((f) => ({ ...f, fecha_desde: e.target.value }))
                  }
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fecha entrega hasta
                </label>
                <input
                  type="date"
                  value={filtros.fecha_hasta}
                  onChange={(e) =>
                    setFiltros((f) => ({ ...f, fecha_hasta: e.target.value }))
                  }
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
              </div>
              {!esCliente && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Clientes ({filtros.cliente_ids.length} seleccionados)
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded p-2 bg-gray-50 dark:bg-gray-900/40 space-y-1">
                    {opcionesFiltro.clientes.length === 0 && (
                      <p className="text-xs text-gray-500">Sin datos</p>
                    )}
                    {opcionesFiltro.clientes.map((c) => (
                      <label
                        key={c.id}
                        className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-white dark:hover:bg-gray-800 px-1 py-0.5 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={filtros.cliente_ids.includes(c.id)}
                          onChange={() =>
                            setFiltros((f) => ({
                              ...f,
                              cliente_ids: toggleId(f.cliente_ids, c.id),
                            }))
                          }
                          className="accent-[#29ABE2]"
                        />
                        <span className="truncate">{c.nombre}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Faenas ({filtros.operacion_ids.length} seleccionadas)
                </label>
                <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded p-2 bg-gray-50 dark:bg-gray-900/40 space-y-1">
                  {opcionesFiltro.operaciones.length === 0 && (
                    <p className="text-xs text-gray-500">Sin datos</p>
                  )}
                  {opcionesFiltro.operaciones
                    .filter(
                      (o) =>
                        filtros.cliente_ids.length === 0 ||
                        filtros.cliente_ids.includes(o.cliente_id)
                    )
                    .map((o) => (
                      <label
                        key={o.id}
                        className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-white dark:hover:bg-gray-800 px-1 py-0.5 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={filtros.operacion_ids.includes(o.id)}
                          onChange={() =>
                            setFiltros((f) => ({
                              ...f,
                              operacion_ids: toggleId(f.operacion_ids, o.id),
                            }))
                          }
                          className="accent-[#29ABE2]"
                        />
                        <span className="truncate">
                          {o.nombre}
                          {!esCliente && (
                            <span className="text-gray-500 ml-1">
                              · {o.cliente_nombre}
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Resumen */}
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {loading
            ? 'Cargando…'
            : `${totalActual} registro${totalActual !== 1 ? 's' : ''}`}
          {tab === 'entregados' && ' · entregados hoy'}
          {tab === 'todos' && ' · vista combinada'}
        </div>

        {!loading && data.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500">
            {tab === 'vigente'
              ? '📦 No hay pedidos en camino esperando despacho.'
              : tab === 'entregados'
                ? '✅ No hay entregas registradas hoy.'
                : '🔎 Sin resultados con estos filtros.'}
          </div>
        )}

        {data.map((cliente) => {
          const totalRegistros = cliente.operaciones.reduce(
            (acc, o) =>
              acc +
              o.pedidos.reduce((a2, p) => a2 + p.registros.length, 0),
            0
          );
          return (
            <section
              key={cliente.cliente_id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow"
            >
              <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="inline-block w-1 h-7 bg-[#F7941D] rounded-full" />
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                      {cliente.cliente_nombre}
                    </h2>
                    {cliente.observaciones_despacho && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {cliente.observaciones_despacho}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {totalRegistros} ítem{totalRegistros !== 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={() => imprimirCliente(cliente)}
                    className="inline-flex items-center px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-600"
                    title="Imprimir cliente (todas las faenas)"
                  >
                    <Printer className="w-3 h-3 mr-1" /> Imprimir
                  </button>
                  {!esCliente && tab !== 'entregados' && (
                    <button
                      onClick={() => abrirModalCliente(cliente)}
                      className="inline-flex items-center px-3 py-1 bg-[#F7941D] hover:bg-[#d97d12] text-white rounded text-xs font-semibold"
                    >
                      <Truck className="w-3 h-3 mr-1" /> Entregar a cliente
                    </button>
                  )}
                </div>
              </header>

              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {cliente.operaciones.map((op) => {
                  const totalOp = op.pedidos.reduce(
                    (acc, p) => acc + p.registros.length,
                    0
                  );
                  // Resumen por segmento (Fase 5, 2026-06-19). Suma
                  // las cantidades de los registros de la operación
                  // por segmento e insumo. Si todos los registros son
                  // segmento NULL (legacy), nada se muestra.
                  const resumenSeg = op.pedidos.reduce(
                    (acc, p) => {
                      for (const r of p.registros) {
                        const seg = r.segmento;
                        if (!seg) continue;
                        const bucket = r.es_insumo
                          ? `${seg}_ins`
                          : `${seg}_rec`;
                        acc[bucket] = (acc[bucket] || 0) + Number(r.cantidad);
                      }
                      return acc;
                    },
                    {} as Record<string, number>
                  );
                  const tieneResumenSeg = Object.keys(resumenSeg).length > 0;
                  return (
                    <div key={op.operacion_id} className="p-4 space-y-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="inline-block w-1 h-5 bg-[#29ABE2] rounded-full" />
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {op.operacion_nombre}
                          </h3>
                          {tieneResumenSeg && (
                            <div
                              className="flex items-center gap-1 ml-2 flex-wrap"
                              data-testid={`resumen-seg-${op.operacion_id}`}
                            >
                              {resumenSeg.dia_rec > 0 && (
                                <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                  ☀ Día {resumenSeg.dia_rec}
                                </span>
                              )}
                              {resumenSeg.noche_rec > 0 && (
                                <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">
                                  ☾ Noche {resumenSeg.noche_rec}
                                </span>
                              )}
                              {(resumenSeg.dia_ins ||
                                resumenSeg.noche_ins ||
                                resumenSeg.transversal_ins) > 0 && (
                                <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                  📦 Insumos{' '}
                                  {(resumenSeg.dia_ins || 0) +
                                    (resumenSeg.noche_ins || 0) +
                                    (resumenSeg.transversal_ins || 0)}
                                </span>
                              )}
                            </div>
                          )}
                          {(op.direccion || op.comuna || op.ciudad) && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {[op.direccion, op.comuna, op.ciudad]
                                .filter(Boolean)
                                .join(', ')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => imprimirOperacion(cliente, op)}
                            className="inline-flex items-center px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-600"
                            title="Imprimir esta faena"
                          >
                            <Printer className="w-3 h-3 mr-1" /> Imprimir
                          </button>
                          {!esCliente && tab !== 'entregados' && (
                            <button
                              onClick={() => abrirModalFaena(cliente, op)}
                              className="inline-flex items-center px-3 py-1 bg-[#29ABE2] hover:bg-[#1f8fbf] text-white rounded text-xs font-semibold"
                            >
                              <Truck className="w-3 h-3 mr-1" /> Entregar a cliente
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-900/40 rounded p-3 space-y-2">
                        {op.pedidos.map((p) => (
                          <div key={p.pedido_id}>
                            <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 flex-wrap">
                              <span className="bg-white dark:bg-gray-800 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-600">
                                {p.pedido_codigo}
                              </span>
                              <span className="text-gray-500">
                                {p.registros.length} ítem
                                {p.registros.length !== 1 ? 's' : ''} ·{' '}
                                {p.fecha_entrega
                                  ? new Date(p.fecha_entrega).toLocaleDateString(
                                      'es-CL'
                                    )
                                  : ''}
                              </span>
                              {/* Fase 6 (2026-07-01): botones de entrega POR TANDA
                                  cuando el pedido tiene segmentos separados. Solo se
                                  muestran si hay registros en 'entregado_a_despacho'
                                  y el rol NO es cliente. Cada tanda tiene además su
                                  propio botón "🖨️ Comprobante Día/Noche" para abrir
                                  la vista de impresión filtrada por ese segmento. */}
                              {!esCliente && p.segmentos && (
                                <div
                                  className="ml-auto flex items-center gap-1 flex-wrap"
                                  data-testid={`tanda-botones-${p.pedido_id}`}
                                >
                                  {(['dia', 'noche'] as const).map((seg) => {
                                    const bucket = p.segmentos?.[seg];
                                    if (!bucket || bucket.registros.length === 0) {
                                      return null;
                                    }
                                    const hayEnDespacho = bucket.registros.some(
                                      (r) =>
                                        r.estado === 'entregado_a_despacho'
                                    );
                                    const yaDespachada =
                                      bucket.estado === 'despachado';

                                    return (
                                      <span
                                        key={seg}
                                        className="inline-flex items-center gap-1"
                                        data-testid={`tanda-${seg}-grupo-${p.pedido_id}`}
                                      >
                                        {yaDespachada && (
                                          <span
                                            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800`}
                                            data-testid={`tanda-${seg}-despachada-${p.pedido_id}`}
                                          >
                                            {seg === 'dia' ? '☀' : '☾'}{' '}
                                            {seg === 'dia' ? 'Día' : 'Noche'}{' '}
                                            despachada ✓
                                          </span>
                                        )}
                                        {hayEnDespacho && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              abrirModalTanda(
                                                p.pedido_id,
                                                p.pedido_codigo,
                                                seg,
                                                bucket.registros.filter(
                                                  (r) =>
                                                    r.estado ===
                                                    'entregado_a_despacho'
                                                ).length
                                              )
                                            }
                                            className={`text-[10px] font-semibold uppercase px-2 py-1 rounded shadow-sm transition ${
                                              seg === 'dia'
                                                ? 'bg-amber-500 hover:bg-amber-600 text-white'
                                                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                            }`}
                                            data-testid={`tanda-${seg}-entregar-${p.pedido_id}`}
                                          >
                                            {seg === 'dia' ? '☀' : '☾'} Entregar{' '}
                                            {seg === 'dia' ? 'Día' : 'Noche'}
                                          </button>
                                        )}
                                        {/* Botón imprimir comprobante FILTRADO al segmento.
                                            Se muestra siempre que la tanda tenga registros
                                            (antes o después de entregar). */}
                                        <button
                                          type="button"
                                          onClick={() =>
                                            imprimirTanda(cliente, op, seg)
                                          }
                                          title={`Imprimir comprobante ${
                                            seg === 'dia' ? 'DÍA' : 'NOCHE'
                                          }`}
                                          className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded border transition ${
                                            seg === 'dia'
                                              ? 'border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-100'
                                              : 'border-indigo-400 text-indigo-800 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-100'
                                          }`}
                                          data-testid={`tanda-${seg}-imprimir-${p.pedido_id}`}
                                        >
                                          <Printer className="w-3 h-3" />
                                          {seg === 'dia' ? 'Día' : 'Noche'}
                                        </button>
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            <ul className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-1 text-xs text-gray-700 dark:text-gray-200">
                              {p.registros.map((r) => {
                                const esEntregado =
                                  r.estado === 'entregado_al_cliente' ||
                                  r.estado === 'despachado';
                                return (
                                  <li
                                    key={r.id}
                                    className={`flex flex-col gap-0.5 bg-white dark:bg-gray-800 px-2 py-1 rounded ${
                                      esEntregado
                                        ? 'border-l-2 border-green-500'
                                        : 'border-l-2 border-[#29ABE2]'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="truncate flex items-center gap-1">
                                        {r.segmento === 'dia' && (
                                          <span
                                            className="text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-amber-200 text-amber-900"
                                            data-testid={`chip-seg-${r.id}`}
                                          >
                                            ☀ Día
                                          </span>
                                        )}
                                        {r.segmento === 'noche' && (
                                          <span
                                            className="text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-indigo-200 text-indigo-900"
                                            data-testid={`chip-seg-${r.id}`}
                                          >
                                            ☾ Noche
                                          </span>
                                        )}
                                        {r.es_insumo && (
                                          <span
                                            className="text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-blue-200 text-blue-900"
                                            data-testid={`chip-ins-${r.id}`}
                                          >
                                            insumo
                                          </span>
                                        )}
                                        <span className="truncate">
                                          {r.receta_nombre}
                                        </span>
                                        {r.tipo_plato && (
                                          <span className="text-[10px] text-gray-500 ml-1">
                                            ({r.tipo_plato})
                                          </span>
                                        )}
                                      </span>
                                      <span className="font-semibold">
                                        × {r.cantidad}
                                      </span>
                                    </div>
                                    {esEntregado && (
                                      <div className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-2 flex-wrap">
                                        <CheckCircle2 className="w-3 h-3 text-green-600" />
                                        {fmtFecha(r.fecha_entrega_cliente)}
                                        {r.entregado_por && (
                                          <span className="flex items-center gap-0.5">
                                            <UserIcon className="w-2.5 h-2.5" />
                                            E:{r.entregado_por}
                                          </span>
                                        )}
                                        {r.recibido_por && (
                                          <span className="flex items-center gap-0.5">
                                            <UserIcon className="w-2.5 h-2.5" />
                                            R:{r.recibido_por}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    {!esEntregado && r.fecha_a_despacho && (
                                      <div className="text-[10px] text-gray-500 flex items-center gap-1">
                                        <Clock className="w-2.5 h-2.5" />
                                        En despacho desde{' '}
                                        {fmtFecha(r.fecha_a_despacho)}
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                        <div className="text-xs text-gray-500 pt-2 border-t border-gray-200 dark:border-gray-700">
                          Total de la faena: {totalOp} ítem
                          {totalOp !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {/* Fase 6+firmas (2026-07-01): Modal para entregar UNA TANDA.
          Espejo visual del modal unificado, pero llama a
          /despacho/pedido/:id/tanda/:seg/entregar y guarda las firmas
          asociadas a esa tanda. */}
      {modalTanda.isOpen && modalTanda.pedidoId !== null && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          data-testid="modal-entrega-tanda"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                {modalTanda.segmento === 'dia' ? '☀' : modalTanda.segmento === 'noche' ? '☾' : '·'}
                Entregar tanda{' '}
                {modalTanda.segmento === 'dia'
                  ? 'DÍA · Almuerzo'
                  : modalTanda.segmento === 'noche'
                  ? 'NOCHE · Cena'
                  : 'TRANSVERSAL'}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Pedido {modalTanda.pedidoCodigo} ·{' '}
                {modalTanda.cantidadRegistros} registro
                {modalTanda.cantidadRegistros !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300 block mb-1">
                  Entrega (despacho)
                </label>
                <input
                  type="text"
                  value={entregaForm.entregado_por}
                  onChange={(e) =>
                    setEntregaForm((f) => ({
                      ...f,
                      entregado_por: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded border-gray-300 dark:border-gray-600 dark:bg-gray-900 text-sm"
                  placeholder="Nombre de quien entrega"
                  data-testid="modal-tanda-entregado-por"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300 block mb-1">
                  Recibe (cliente)
                </label>
                <input
                  type="text"
                  value={entregaForm.recibido_por}
                  onChange={(e) =>
                    setEntregaForm((f) => ({
                      ...f,
                      recibido_por: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded border-gray-300 dark:border-gray-600 dark:bg-gray-900 text-sm"
                  placeholder="Nombre de quien recibe"
                  data-testid="modal-tanda-recibido-por"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300 block mb-1">
                  Comentario (opcional)
                </label>
                <textarea
                  value={entregaForm.comentario}
                  onChange={(e) =>
                    setEntregaForm((f) => ({
                      ...f,
                      comentario: e.target.value,
                    }))
                  }
                  rows={2}
                  className="w-full px-3 py-2 border rounded border-gray-300 dark:border-gray-600 dark:bg-gray-900 text-sm"
                  placeholder="Observaciones de la entrega"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={cerrarModalTanda}
                className="px-4 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmarEntregarTanda}
                className={`px-4 py-2 text-sm font-semibold rounded text-white shadow ${
                  modalTanda.segmento === 'dia'
                    ? 'bg-amber-500 hover:bg-amber-600'
                    : modalTanda.segmento === 'noche'
                    ? 'bg-indigo-600 hover:bg-indigo-700'
                    : 'bg-slate-600 hover:bg-slate-700'
                }`}
                data-testid="modal-tanda-confirmar"
              >
                {modalTanda.segmento === 'dia' ? '☀' : modalTanda.segmento === 'noche' ? '☾' : ''}
                Confirmar entrega
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: entregar al cliente */}
      {modal.isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Entregar al cliente
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {modal.titulo}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Se marcarán {modal.registroIds.length} registro
                {modal.registroIds.length !== 1 ? 's' : ''} como entregados.
                Estado terminal — no se puede revertir.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Entrega (despachador)
                </label>
                <input
                  type="text"
                  value={entregaForm.entregado_por}
                  onChange={(e) =>
                    setEntregaForm((f) => ({
                      ...f,
                      entregado_por: e.target.value,
                    }))
                  }
                  placeholder="Nombre de quien entrega"
                  maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-[#29ABE2] focus:border-[#29ABE2]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Recibe (cliente)
                </label>
                <input
                  type="text"
                  value={entregaForm.recibido_por}
                  onChange={(e) =>
                    setEntregaForm((f) => ({
                      ...f,
                      recibido_por: e.target.value,
                    }))
                  }
                  placeholder="Nombre de quien recibe"
                  maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-[#29ABE2] focus:border-[#29ABE2]"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Comentario (opcional)
              </label>
              <textarea
                value={entregaForm.comentario}
                onChange={(e) =>
                  setEntregaForm((f) => ({ ...f, comentario: e.target.value }))
                }
                placeholder="Anotaciones de entrega — opcional"
                rows={3}
                maxLength={1000}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-[#29ABE2] focus:border-[#29ABE2]"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() =>
                  setModal({ isOpen: false, titulo: '', registroIds: [] })
                }
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarEntregar}
                disabled={trabajando.size > 0}
                className="px-4 py-2 bg-[#F7941D] hover:bg-[#d97d12] text-white rounded text-sm font-semibold disabled:opacity-50"
              >
                Confirmar entrega
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
